import { useCallback, useMemo, useState } from 'react';
import useApi from '../../../hooks/useApi';
import ForceEditTriggerDialog from './ForceEditTriggerDialog';

const getRowKey = (row) => row?.__rowId || row?.id || row?.rowId || row?.bac || null;

const useForceTriggerOverride = () => {
  const { useEmailTriggers } = useApi();
  const { data: triggerData, isLoading: triggerLoading } = useEmailTriggers();

  const [overrides, setOverrides] = useState({});
  const [open, setOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [selectedTriggerId, setSelectedTriggerId] = useState('');
  const [error, setError] = useState('');

  const activeTriggers = useMemo(
    () => (Array.isArray(triggerData) ? triggerData : []).filter((trigger) => trigger?.isActive !== false),
    [triggerData]
  );

  const triggerOptions = useMemo(() => {
    const programName = activeRow?.programName;
    return activeTriggers
      .filter((trigger) => !programName || !trigger?.programName || trigger.programName === programName)
      .sort((left, right) => String(left?.name || '').localeCompare(String(right?.name || '')));
  }, [activeTriggers, activeRow]);

  const openDialog = (row) => {
    const key = getRowKey(row);
    const override = key ? overrides[key] : null;
    setActiveRow(row);
    setSelectedTriggerId(override?.id || row?.emailTriggerId || '');
    setError('');
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setActiveRow(null);
    setSelectedTriggerId('');
    setError('');
  };

  const submit = () => {
    if (!activeRow || !selectedTriggerId) {
      setError('Select a trigger to apply.');
      return;
    }
    const trigger = activeTriggers.find((entry) => entry.id === selectedTriggerId);
    if (!trigger) {
      setError('Selected trigger not found.');
      return;
    }
    const key = getRowKey(activeRow);
    if (!key) {
      setError('Unable to resolve row key.');
      return;
    }
    setOverrides((prev) => ({
      ...prev,
      [key]: { id: trigger.id, name: trigger.name || trigger.id },
    }));
    closeDialog();
  };

  const clearOverride = () => {
    if (!activeRow) {
      closeDialog();
      return;
    }
    const key = getRowKey(activeRow);
    if (!key) {
      closeDialog();
      return;
    }
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    closeDialog();
  };

  const applyOverrides = useCallback(
    (rows) =>
      rows.map((row) => {
        const key = getRowKey(row);
        const override = key ? overrides[key] : null;
        if (!override) return row;
        return {
          ...row,
          emailTriggerId: override.id,
          emailTriggerName: override.name,
          emailTriggerOverride: true,
        };
      }),
    [overrides]
  );

  const handleRowAction = (action, row) => {
    if (action?.type !== 'force_edit_trigger') return false;
    openDialog(row);
    return true;
  };

  const dialog = (
    <ForceEditTriggerDialog
      open={open}
      row={activeRow}
      triggerId={selectedTriggerId}
      triggerOptions={triggerOptions}
      loadingTriggers={triggerLoading}
      error={error}
      onTriggerChange={setSelectedTriggerId}
      onClose={closeDialog}
      onSubmit={submit}
      onClear={Object.keys(overrides).length ? clearOverride : null}
    />
  );

  return {
    applyOverrides,
    handleRowAction,
    dialog,
  };
};

export default useForceTriggerOverride;
